import type { ReactNode } from "react";
import { useT } from "../../i18n/index.js";

/**
 * F12 — Barra de ações em massa no rodapé. Aparece quando `count > 0`.
 *
 * CEO 2026-10-12 ("o card com as opções fica estranho quando abre"): a barra
 * era centralizada na JANELA com até 1160 px — num notebook ficava por cima
 * do menu lateral, e os grupos caíam um por linha. Agora:
 *   - fica só na coluna do conteúdo (à direita do menu de 250 px; no
 *     celular, largura toda);
 *   - cabeçalho fixo: contagem + resumo à esquerda, "Limpar" à direita;
 *   - grupos lado a lado, cada grupo numa linha só (rótulo + botões sem
 *     quebrar), e quebram como blocos inteiros;
 *   - altura máxima com rolagem; um espaçador no fluxo da página impede que
 *     a barra cubra as últimas linhas da lista.
 *
 * Caller passa os botões como children (normalmente dentro de `BulkGroup`).
 */
export interface BulkActionBarProps {
  count: number;
  onClear: () => void;
  /** Texto opcional pra contextualizar (ex.: "12 dossiers"). Default: "X selecionados". */
  label?: string;
  /** Linha de resumo opcional (ex.: totais) exibida ao lado da contagem. */
  summary?: ReactNode;
  children?: ReactNode;
}

export function BulkActionBar({ count, onClear, label, summary, children }: BulkActionBarProps) {
  const t = useT();
  if (count === 0) return null;
  const text =
    label ??
    `${count} ${count === 1 ? t("bulkActionBar.selectedOne") : t("bulkActionBar.selectedMany")}`;
  return (
    <>
      {/* Espaço no fim da página para a barra não esconder as últimas linhas. */}
      <div aria-hidden="true" className="h-44" />
      <div
        className="fixed bottom-3 left-3 right-3 z-40 min-[901px]:left-[88px] min-[901px]:right-5"
        data-bulk-action-bar
        role="region"
        aria-label={text}
      >
        <div className="mx-auto max-w-[1180px] overflow-hidden rounded-xl border border-lime/40 bg-neutral-900 shadow-2xl">
          <div className="flex items-center justify-between gap-3 border-b border-neutral-800 px-3 py-1.5">
            <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-0.5">
              <span className="whitespace-nowrap text-xs font-semibold">{text}</span>
              {summary && <span className="min-w-0 text-[11px] text-neutral-400">{summary}</span>}
            </div>
            <button
              onClick={onClear}
              className="shrink-0 whitespace-nowrap rounded-md border border-neutral-700 px-2 py-1 text-[11px] text-neutral-300 hover:border-neutral-500"
              title={t("bulkActionBar.clearSelectionTitle")}
            >
              ✕ {t("bulkActionBar.clear")}
            </button>
          </div>
          <div className="flex max-h-[38vh] flex-wrap items-stretch gap-1.5 overflow-y-auto px-3 py-2">
            {children}
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * Grupo de ações da barra: rótulo curto + botões numa linha só. O grupo
 * inteiro quebra para a linha de baixo quando não cabe (nunca no meio).
 */
export function BulkGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex max-w-full items-center gap-1.5 rounded-md border border-neutral-800 bg-neutral-950/40 py-1 pl-2 pr-1 [&_button]:whitespace-nowrap [&_button]:rounded [&_button]:px-2 [&_button]:py-0.5 [&_button]:text-[11px]">
      <span className="whitespace-nowrap text-[9px] uppercase leading-none tracking-wider text-neutral-500">
        {label}
      </span>
      {/* No celular, um grupo largo quebra os PRÓPRIOS botões (nunca sai da barra). */}
      <div className="flex min-w-0 flex-wrap items-center gap-1">{children}</div>
    </div>
  );
}
